import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	IconButton,
	Paper
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InputLabel from '../../Pages/NewEsign/Estamp/input-label.js';
import DropDown from '../drop-down.js';
import { CommonDropdownStyles } from '../../../utils/util.js';
import DateIcon from '../../../Assets/Images/DateIcon.svg';
import GeneralModal from './GeneralModal/general-modal.js';

const FIELD_LABELS = {
	firstName: 'First Name',
	middleName: 'Middle Name',
	lastName: 'Last Name',
	emailId: 'Email ID',
	mobileNumber: 'Mobile Number',
	pincode: 'Pincode',
	city: 'City',
	state: 'State',
	country: 'Country',
	recipientDob: 'Year of Birth',
	recipientAadhar: 'Aadhar (Last 4)',
	recipientGender: 'Gender',
	nameMatchPercentage: 'Name Match Percentage'
};

const FIELD_ORDER = [
	'firstName',
	'middleName',
	'lastName',
	'emailId',
	'mobileNumber',
	'pincode',
	'city',
	'state',
	'country',
	'recipientDob',
	'recipientAadhar',
	'recipientGender',
	'nameMatchPercentage'
];

const getRecipientLabel = (recipient) => {
	const { firstName, lastName, emailId } = recipient?.editData?.[0]?.newState || {};
	if (firstName || lastName) {
		return `${firstName || ''} ${lastName || ''}`.trim();
	}

	if (emailId) {
		return emailId;
	}

	return 'Unnamed Recipient';
};

const EditHistoryModal = ({ open, onClose, recipientHistories = [] }) => {
	const [selectedRecipientIndex, setSelectedRecipientIndex] = useState(0);
	const [selectedEditIndex, setSelectedEditIndex] = useState(0);

	const recipient = recipientHistories[selectedRecipientIndex];
	const editHistory = recipient?.editData || [];
	const selectedItem = editHistory[selectedEditIndex] || {};

	const handlePrevClick = () => {
		if (selectedEditIndex > 0) {
			setSelectedEditIndex(selectedEditIndex - 1);
		}
	};

	const handleNextClick = () => {
		if (selectedEditIndex < editHistory.length - 1) {
			setSelectedEditIndex(selectedEditIndex + 1);
		}
	};

	const createdAt = selectedItem?.updatedAt || 'N/A';

	return (
		<GeneralModal
			open={open}
			onClose={onClose}
			btn2OnClick={onClose}
			btn2Text='Close'
			width={{ xs: '100%', sm: '90%', md: '800px' }}
			padding={{ xs: '16px', sm: '24px', md: '32px' }}
			maxHeight='74vh'
		>
			<Typography sx={{ fontWeight: 600, fontSize: { xs: '16px', sm: '18px' }, mb: 2 }}>
		Edit History
			</Typography>

			{recipientHistories.length === 0 ? (
				<Typography variant='body2' color='text.secondary'>No data available.</Typography>
			) : (
				<>
					{/* Recipient Dropdown */}
					<Box sx={{ mb: 3 }}>
						<InputLabel name='Select Recipient' />
						<DropDown
							{...CommonDropdownStyles}
							width='100%'
							placeholder='Select Recipient'
							dropdownValue={recipientHistories.map((history) => getRecipientLabel(history))}
							handleChange={(e) => {
								const selectedLabel = e.target.value;
								const index = recipientHistories.findIndex(
									(recipient) => getRecipientLabel(recipient) === selectedLabel
								);
								if (index !== -1) {
									setSelectedRecipientIndex(index);
									setSelectedEditIndex(0);
								}
							}}
							dropDownSelected={getRecipientLabel(recipientHistories[selectedRecipientIndex])}
						/>
					</Box>

					{/* Timestamp and Navigation */}
					<Box
						display='flex'
						flexDirection={{ xs: 'column', sm: 'row' }}
						justifyContent='space-between'
						alignItems={{ xs: 'flex-start', sm: 'center' }}
						sx={{ mb: 3, gap: 1 }}
					>
						<Box display='flex' alignItems='center' gap='10px'>
							<img src={DateIcon} alt='DateIcon' height='50px' width='50px' style={{borderRadius: '7px'}}/>
							<Typography variant='body2' color='text.secondary'>
						Updated at: {createdAt}
							</Typography>
						</Box>
						<Box display='flex' alignItems='center' gap={1}>
							<IconButton onClick={handlePrevClick} disabled={selectedEditIndex === 0}>
								<ArrowBackIosNewIcon fontSize='small' />
							</IconButton>
							<Typography variant='body2' color='text.secondary'>
								{selectedEditIndex + 1} of {editHistory.length}
							</Typography>
							<IconButton
								onClick={handleNextClick}
								disabled={selectedEditIndex === editHistory.length - 1}
							>
								<ArrowForwardIosIcon fontSize='small' />
							</IconButton>
						</Box>
					</Box>

					{/* Responsive Table Wrapper */}
					<Box sx={{ overflowX: 'auto' }}>
						<Paper variant='outlined' sx={{ borderRadius: '8px', minWidth: '600px' }}>
							<Table size='small' sx={{ border: 'none', borderRadius: '8px'}}>
								<TableHead>
									<TableRow sx={{ backgroundColor: '#ecf0e6', height: '55px' }}>
										{['Field', 'Previous', 'Current'].map((label, index, arr) => (
											<TableCell
												key={label}
												sx={{
													fontWeight: 'bold',
													position: 'relative',
													'&::after': index !== arr.length - 1 && {
														content: '""',
														position: 'absolute',
														top: '25%',
														right: 0,
														width: '1px',
														height: '50%',
														backgroundColor: '#ccc'
													}
												}}
											>
												{label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{FIELD_ORDER.filter((key) => {
										const prev = selectedItem.previousState?.[key];
										const curr = selectedItem.newState?.[key];
										return prev || curr;
									}).map((key) => {
										const previousValue = selectedItem.previousState?.[key] || '-';
										const currentValue = selectedItem.newState?.[key] || '-';

										return (
											<TableRow
												key={key}
												sx={{
													height: '55px'
												}}
											>
												<TableCell>{FIELD_LABELS[key] || key}</TableCell>
												<TableCell>{previousValue}</TableCell>
												<TableCell>{currentValue}</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</Paper>
					</Box>
				</>
			)}
		</GeneralModal>

	);
};

EditHistoryModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	recipientHistories: PropTypes.array
};

export default EditHistoryModal;
